import {
  useState,
  useRef,
  useEffect,
  createRef,
  useCallback,
  useMemo,
} from "react";
import BoundingBox from "../Annotations/BoundingBox";
import PlusIcon from "../Icons/PlusIcon";
import MinusIcon from "../Icons/MinusIcon";
import ArrowLeftIcon from "../Icons/ArrowLeftIcon";
import ArrowRightIcon from "../Icons/ArrowRightIcon";
import RotateLeftIcon from "../Icons/RotateLeftIcon";
import RotateRightIcon from "../Icons/RotateRightIcon";
import ExpandIcon from "../Icons/ExpandIcon";
import TrueIcon from "../Icons/TrueIcon";
import Button from "../Buttons/Button";
import DomUtils from "../../utils/dom.utils";

const Viewer = ({
  className,
  sx,
  images,
  index: _index,
  coordinates,
  dimensions,
  isFullScreen,
  multi,
  drawCallback,
}) => {
  const [index, setIndex] = useState(0);
  const indexTriggeredBy = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [currentPoint, setCurrentPoint] = useState({ x: 0, y: 0 });
  const imageRefs = useRef([]);
  const containerRef = useRef(null);
  imageRefs.current = images.map((_, i) => imageRefs.current[i] ?? createRef());
  const boundingBoxRef = useRef(null);

  const updateIndex = useCallback((index, triggeredBy) => {
    setIndex(index);
    indexTriggeredBy.current = triggeredBy;
  }, []);

  const onExpand = () => {
    const queryParams = new URLSearchParams();
    if (images) queryParams.append("images", JSON.stringify(images));
    if (index !== undefined) queryParams.append("index", JSON.stringify(index));
    if (coordinates)
      queryParams.append("coordinates", JSON.stringify(coordinates));
    if (dimensions)
      queryParams.append("dimensions", JSON.stringify(dimensions));

    window.open(`/focus?${queryParams.toString()}`, "_blank");
  };

  const drawingCoordinates = useMemo(() => {
    if (!isDrawing) return null;
    const rect = imageRefs.current[index].current.getBoundingClientRect();
    const x1 = Math.min(startPoint.x, currentPoint.x);
    const y1 = Math.min(startPoint.y, currentPoint.y);
    const x2 = Math.max(startPoint.x, currentPoint.x);
    const y2 = Math.max(startPoint.y, currentPoint.y);
    const x1Ratio = x1 / rect.width;
    const y1Ratio = y1 / rect.height;
    const x2Ratio = x2 / rect.width;
    const y2Ratio = y2 / rect.height;
    return {
      x1: x1Ratio,
      y1: y1Ratio,
      x2: x2Ratio,
      y2: y2Ratio,
    };
  }, [isDrawing, startPoint, currentPoint, index]);

  const handleMouseDown = (e) => {
    const rect = imageRefs.current[index].current.getBoundingClientRect();
    setStartPoint({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setCurrentPoint({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDrawing(true);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const rect = imageRefs.current[index].current.getBoundingClientRect();
    setCurrentPoint({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseUp = () => {
    if (!drawCallback || !isDrawing) return;
    setIsDrawing(false);
    const width = drawingCoordinates.x2 - drawingCoordinates.x1;
    const height = drawingCoordinates.y2 - drawingCoordinates.y1;
    const MIN_SIZE = 0.01;
    if (width > MIN_SIZE && height > MIN_SIZE) {
      drawCallback(index, drawingCoordinates);
    }
  };

  // new images
  useEffect(() => {
    setRotate(0);
    setZoom(1);
    updateIndex(0, "new");
  }, [images, updateIndex]);

  useEffect(() => {
    setTimeout(() => {
      if (_index >= 0 && _index < images.length) {
        updateIndex(_index, "new");
      }
    }, 1500);
  }, [_index, updateIndex, images]);

  // index changed
  useEffect(() => {
    switch (indexTriggeredBy.current) {
      // don't auto-scroll when manually scrolling
      case "scroll":
        break;
      // scroll will be handled by the coordinates not the index
      case "coordinates":
        break;
      default:
        imageRefs.current[index]?.current.scrollIntoView({
          // behavior: "smooth",
          block: multi ? "nearest" : undefined,
        });
        break;
    }
  }, [index, multi]);

  useEffect(() => {
    // coordinates changed
    if (coordinates) {
      updateIndex(_index, "coordinates");
      DomUtils.updated(() => {
        boundingBoxRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      });
      return;
    }
  }, [_index, coordinates, updateIndex]);

  // update index on scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const containerRect = container.getBoundingClientRect();
        const containerTop = containerRect.top;
        const containerHeight = containerRect.height;
        const containerMiddle = containerTop + containerHeight / 2;
        let minDistance = Infinity;
        let closestIndex = -1;
        imageRefs.current.forEach((imgRef, i) => {
          const imgElement = imgRef.current;
          if (imgElement) {
            const imgRect = imgElement.getBoundingClientRect();
            const imgMiddle = imgRect.top + imgRect.height / 2;
            const distance = Math.abs(imgMiddle - containerMiddle);

            if (distance < minDistance) {
              minDistance = distance;
              closestIndex = i;
            }
          }
        });
        if (closestIndex !== -1 && closestIndex !== index) {
          updateIndex(closestIndex, "scroll");
        }
      }, 100);
    };
    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [index, updateIndex]);

  // sub-components
  const ViewerControls = () => {
    const iconSize = 20;
    return (
      <div className="w-full flex justify-center">
        <div className="flex-center gap-20 pad-5 w-fit br-5 border-white overflow-hidden">
          {!isFullScreen && <TrueIcon size={iconSize} color={"black"} />}
          <div className="flex gap-5">
            <Button
              className={"blue"}
              onClick={() => setZoom((zoom) => zoom + 0.2)}
              disabled={zoom >= 3}
            >
              <PlusIcon size={iconSize} title={"Zoom In"} />
            </Button>
            <Button
              className={"blue"}
              onClick={() => setZoom((zoom) => zoom - 0.2)}
              disabled={zoom <= 0.2}
            >
              <MinusIcon size={iconSize} title={"Zoom Out"} />
            </Button>
          </div>
          <div className="flex gap-5">
            <ArrowLeftIcon
              title={"Previous"}
              className={"button orange"}
              size={iconSize}
              onClick={() => updateIndex(index - 1, "click")}
              disabled={index <= 0}
            />
            <div className="nowrap bg-white pad-0-5 font-14 serif br-5 bold">{`${
              images.length ? index + 1 : "-"
            } / ${images.length || "-"}`}</div>
            <ArrowRightIcon
              title={"Next"}
              className={"button orange"}
              size={iconSize}
              onClick={() => updateIndex(index + 1, "click")}
              disabled={index >= images.length - 1}
            />
          </div>
          <div className="flex gap-5">
            <RotateLeftIcon
              size={iconSize}
              className={"button blue"}
              onClick={() => setRotate((rotate) => (rotate + 270) % 360)}
            />
            <RotateRightIcon
              size={iconSize}
              className={"button blue"}
              onClick={() => setRotate((rotate) => (rotate + 90) % 360)}
            />
          </div>
          {!isFullScreen && (
            <ExpandIcon
              className={"button blue"}
              size={iconSize}
              onClick={onExpand}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`box-shadow s-full flex-col bg-white border-top-green pad-5 ${className}`}
      style={sx}
    >
      <div className="bg-lg s-full flex-col pad-5 gap-5">
        {ViewerControls()}
        <div
          ref={containerRef}
          className="overflow-auto scrollbar flex-col gap-px"
        >
          {images.map((img, i) => (
            <div
              key={img.src}
              className={`relative`}
              onMouseDown={(e) => drawCallback && handleMouseDown(e)}
              onMouseMove={(e) => drawCallback && handleMouseMove(e)}
              onMouseUp={(e) => drawCallback && handleMouseUp(e)}
              onMouseLeave={() =>
                drawCallback && isDrawing && setIsDrawing(false)
              }
            >
              <img
                className={`r-${rotate} select-none drag-none`}
                ref={imageRefs.current[i]}
                src={img.src}
                alt={`page-${i + 1}`}
                style={{
                  width: `${zoom * 100}%`,
                  transition: "width 0.2s ease",
                }}
              />
              {_index === i && (
                <BoundingBox
                  className="absolute"
                  ref={boundingBoxRef}
                  rotate={rotate}
                  coordinates={coordinates}
                  dimensions={dimensions}
                  zoom={zoom}
                />
              )}
              {drawCallback && index === i && isDrawing && (
                <div
                  className="bounding-box absolute"
                  style={{
                    left: `${drawingCoordinates.x1 * 100 * zoom}%`,
                    top: `${drawingCoordinates.y1 * 100}%`,
                    width: `${
                      (drawingCoordinates.x2 - drawingCoordinates.x1) *
                      100 *
                      zoom
                    }%`,
                    height: `${
                      (drawingCoordinates.y2 - drawingCoordinates.y1) * 100
                    }%`,
                  }}
                ></div>
              )}
            </div>
          ))}

          <div />
        </div>
      </div>
    </div>
  );
};

Viewer.defaultProps = {
  images: [],
  index: 0,
  className: "",
  sx: {},
  isFullScreen: false,
};

export default Viewer;
